import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Droplets, AlertTriangle, Wind } from 'lucide-react';
import { RecordCard, MetricBox, StatusBadge } from '@/features/shared/components/records';
import type { MaintenanceCheck } from '@/api/maintenanceApi';
import { buildRoute } from '@/shared/lib/constants';
import { format } from 'date-fns';

interface MaintenanceRecordCardProps {
  check: MaintenanceCheck;
  farmId: string;
  pondName?: string;
}

export const MaintenanceRecordCard: React.FC<MaintenanceRecordCardProps> = ({
  check,
  farmId,
  pondName,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(buildRoute.maintenanceDetail(farmId, check.id, check.pondId));
  };

  // Determine if there are any issues
  const hasEquipmentIssue = !check.paddleWheelFunctioning || !check.waterFlowCheck;
  const hasEnvironmentalIssue = check.algalBloom || check.presenceOfBirds || check.stressedFishAtInlets;
  const hasAnyIssue = hasEquipmentIssue || hasEnvironmentalIssue;

  return (
    <RecordCard
      icon={Wrench}
      title="Maintenance Check"
      subtitle={`${pondName || check.pond?.name || `Pond ${check.pondId.slice(0, 8)}`} • ${format(new Date(check.checkedAt), 'PPpp')}`}
      topRight={
        <>
          {hasEquipmentIssue && (
            <StatusBadge variant="error">Equipment Issue</StatusBadge>
          )}
          {hasEnvironmentalIssue && (
            <StatusBadge variant="warning">Environmental</StatusBadge>
          )}
          {!hasAnyIssue && (
            <StatusBadge variant="success">All Good</StatusBadge>
          )}
        </>
      }
      metricsGrid={
        <>
          <MetricBox
            label="Water Level"
            value={`${check.waterLevel.toFixed(2)}m`}
            icon={Droplets}
            color="blue"
          />
          <MetricBox
            label="Paddle Wheel"
            value={check.paddleWheelFunctioning ? 'OK' : 'Issue'}
            icon={Wind}
            color={check.paddleWheelFunctioning ? 'emerald' : 'red'}
          />
          {check.dischargePh !== undefined && (
            <MetricBox
              label="Discharge pH"
              value={check.dischargePh.toFixed(1)}
              color={check.dischargePh >= 6.5 && check.dischargePh <= 8.5 ? 'emerald' : 'amber'}
            />
          )}
          {check.dischargeDo !== undefined && (
            <MetricBox
              label="Discharge DO"
              value={`${check.dischargeDo.toFixed(1)} mg/L`}
              color={check.dischargeDo >= 5 ? 'emerald' : 'amber'}
            />
          )}
        </>
      }
      alert={
        hasEnvironmentalIssue ? (
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            <span>
              {check.algalBloom && 'Algal bloom detected'}
              {check.algalBloom && check.presenceOfBirds && ' • '}
              {check.presenceOfBirds && 'Birds present'}
              {(check.algalBloom || check.presenceOfBirds) && check.stressedFishAtInlets && ' • '}
              {check.stressedFishAtInlets && 'Stressed fish at inlets'}
            </span>
          </div>
        ) : undefined
      }
      footer={
        check.notes ? (
          <p className="line-clamp-2">{check.notes}</p>
        ) : (
          <p className="italic text-slate-400">No notes</p>
        )
      }
      onClick={handleClick}
    />
  );
};
